import emojiRegex from 'emoji-regex';
import { isEmpty, isNil } from 'lodash';
import { useContext } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import ChatBubblesSquareIcon from '@shared/assets/icons/v2/chat-bubbles-square.svg';
import QuestionMarkIcon from '@shared/assets/icons/v2/question-mark.svg';
import { MBView } from '@shared/components/MBView';
import { MBEmojiIcon } from '@shared/components/form/emojiPicker/icon';
import { MBLoadingIndicator } from '@shared/components/loading';
import { ColorThemeContext, Colors } from '@shared/styles';
import { API } from '@shared/types/api';
import { useDmChannelUsers } from '@shared/util/hooks';
import { MBImage } from './MBImage';
import { MBBoxedIcon } from './icon';
import { MBImageURISource } from './props';
import { Styles } from './styles';
import { MBUserCollage } from './user/collage';
import { MBUserImage } from './user/profile';

type ImageSize = 'large' | 'normal' | 'small';

export type ChannelImageInfo = Pick<
  API.ChannelFragment,
  'id' | 'name' | 'type' | 'slug' | 'imageUrl'
>;

interface MBChannelImageProps {
  channel?: ChannelImageInfo | null;
  size?: ImageSize;
  style?: StyleProp<ViewStyle>;
  showLoading?: boolean;
}

export function MBChannelImage({
  channel,
  style,
  size = 'normal',
  showLoading,
}: MBChannelImageProps) {
  const colors = useContext(ColorThemeContext);
  const imageStyle = getImageStyle(size);

  if (isNil(channel)) {
    return (
      <MBView center style={[imageStyle, { backgroundColor: Colors.v2.gray10 }, style]}>
        {showLoading && <MBLoadingIndicator />}
      </MBView>
    );
  }

  if (!isNil(channel.imageUrl) && !isEmpty(channel.imageUrl)) {
    return <MBImage source={{ uri: channel.imageUrl }} style={[imageStyle, style]} />;
  }

  // Only use emoji as channel image if it's at position 0
  // Ignore emojis in middle or end of the channel name
  const emojiMatch = emojiRegex().exec(channel.name);
  if (!isNil(emojiMatch) && emojiMatch.index === 0) {
    return (
      <MBView center style={[imageStyle, { backgroundColor: colors.avatar.background }, style]}>
        <MBEmojiIcon emoji={emojiMatch[0]} size={size} />
      </MBView>
    );
  }

  if (channel.type === 'SUPPORT') {
    return <Icon source={QuestionMarkIcon} size={size} style={style} />;
  }

  if (channel.type === 'DM') {
    return (
      <DirectMessageImage
        channelId={channel.id}
        size={size}
        style={style}
        imageStyle={imageStyle}
      />
    );
  }

  return <DefaultImage size={size} style={style} />;
}

function getImageStyle(size: ImageSize) {
  switch (size) {
    case 'small':
      return Styles.channelImageSmall;
    case 'normal':
      return Styles.channelImage;
    case 'large':
      return Styles.channelImageLarge;
  }
}

function DirectMessageImage({
  channelId,
  size = 'normal',
  style,
  imageStyle,
}: {
  channelId: number;
  size: ImageSize;
  style?: StyleProp<ViewStyle>;
  imageStyle: StyleProp<ViewStyle>;
}) {
  const { users } = useDmChannelUsers(channelId);
  const otherUsers = users.filter(user => !user.isMe);

  if (otherUsers.length > 1) {
    return (
      <MBUserCollage
        users={otherUsers}
        size={size === 'small' ? 'small' : 'normal'}
        style={[imageStyle, style]}
      />
    );
  }

  const otherUser = otherUsers[0];
  if (otherUser) {
    return (
      <MBView style={[imageStyle, style]}>
        <MBUserImage user={otherUser} size={size} />
      </MBView>
    );
  }

  return <DefaultImage size={size} style={style} />;
}

function DefaultImage({ size, style }: { size: ImageSize; style?: StyleProp<ViewStyle> }) {
  return <Icon source={ChatBubblesSquareIcon} size={size} style={style} />;
}

interface IconProps {
  source: MBImageURISource;
  size: ImageSize;
  style?: StyleProp<ViewStyle>;
}

function Icon({ source, size, style }: IconProps) {
  const colors = useContext(ColorThemeContext);
  return (
    <MBBoxedIcon
      source={source}
      color={colors.avatar.foreground}
      backgroundColor={colors.avatar.background}
      size={size}
      style={style}
    />
  );
}
